import React from 'react';
import { Link } from 'react-router-dom';
import ArticleShell from './ArticleShell';
import { H2, H3, P, Callout, BulletList } from './academyPrimitives';
import { getGuideBySlug } from '../../data/knowledgeAcademy';

const FIU_GUIDE = '/knowledge-academy/uae-fiu-regulation-1-2026-suspicious-transactions-freezing-funds';
const REGULATION_PDF = 'https://assets.adgm.com/download/assets/UAEFIU+Regulation+on+Suspension+and+Freezing+Powers+To+GS-NAMLCFTPC+27-04-2026.pdf/f82ef204648a11f18e27525460f38ba0';
const FIU_ANNOUNCEMENT = 'https://ae.linkedin.com/posts/financial-intelligence-unit_uaefiu-regulation-no1-of-2026-activity-7520429837145227264-5gC-';
const linkCls = 'text-teal-700 font-semibold underline underline-offset-2 hover:text-teal-900';

const UAEGoAMLComplianceGuide = () => {
  const guide = {
    ...getGuideBySlug('uae-goaml-aml-compliance-guide'),
    relatedService: { label: 'goAML Compliance', path: '/services/goaml-compliance' }
  };

  const toc = [
    { id: 'introduction', label: 'Introduction' },
    { id: 'what-is-goaml', label: 'What is goAML?' },
    { id: 'who-must-register', label: 'Who Must Register?' },
    { id: 'cdd', label: 'Customer Due Diligence (CDD)' },
    { id: 'edd', label: 'Enhanced Due Diligence (EDD)' },
    { id: 'str', label: 'Suspicious Transaction Reporting' },
    { id: 'urgent-reporting', label: 'Urgent Reporting and UAEFIU Orders' },
    { id: 'aml-policies', label: 'AML Policies & Procedures' },
    { id: 'risk-assessment', label: 'Risk Assessment' },
    { id: 'internal-audit', label: 'AML Internal Audit' },
    { id: 'mistakes', label: 'Common AML Mistakes' },
    { id: 'wpaa-help', label: 'How WPAA Can Help' },
    { id: 'sources', label: 'Sources' }
  ];

  return (
    <ArticleShell guide={guide} toc={toc} testId="ka-article-goaml" ctaService="goaml">
      <Callout variant="info" title="A note on this guide">
        AML and goAML rules are updated periodically by the UAE authorities and the Financial Intelligence Unit
        (FIU). This guide covers the enduring fundamentals every DNFBP needs to know. For anything specific
        to your business, or for the latest FIU rules, please book a free consultation with our team.
      </Callout>

      <H2 id="introduction">Introduction</H2>
      <P>
        Anti-Money Laundering (AML) compliance is a legal obligation for a wide range of UAE businesses.
        The <strong>goAML</strong> platform is the primary channel through which UAE businesses register with
        the Financial Intelligence Unit and submit Suspicious Transaction Reports. Non-compliance can lead to
        significant administrative penalties and reputational damage.
      </P>

      <H2 id="what-is-goaml">What is goAML?</H2>
      <P>
        <strong>goAML</strong> is the UAE reporting platform used to register with the Financial Intelligence
        Unit and submit Suspicious Transaction Reports (STRs), Suspicious Activity Reports (SARs) and other
        regulatory notifications. Every Designated Non-Financial Business or Profession (DNFBP) is required to
        register on goAML.
      </P>

      <H2 id="who-must-register">Who Must Register?</H2>
      <p className="text-gray-700 mb-2">Common <strong>DNFBPs</strong> (Designated Non-Financial Businesses and Professions) required to register on goAML include:</p>
      <BulletList items={[
        'Auditors and accountants',
        'Real estate agents and brokers',
        'Dealers in precious metals and stones',
        'Corporate service providers',
        'Legal consultants and law firms',
        'Trust and company service providers'
      ]} />
      <Callout variant="warning" title="Registration is mandatory">
        Failing to register on goAML is itself an administrative violation. Registration must be completed
        before you begin providing services to clients falling within scope of the AML regulations.
      </Callout>

      <H2 id="cdd">Customer Due Diligence (CDD)</H2>
      <P>CDD is the foundation of AML compliance. Businesses must identify and document every client before onboarding, and keep the information up to date.</P>
      <p className="text-gray-700 mb-2">Standard CDD includes:</p>
      <BulletList items={[
        'Verifying customer identity using reliable documents',
        'Identifying and verifying the beneficial owner',
        'Understanding the purpose and nature of the business relationship and obtaining information on the source of funds and, where appropriate, source of wealth, based on the customer’s risk profile and applicable requirements',
        'Assigning a risk rating to the customer'
      ]} />

      <H2 id="edd">Enhanced Due Diligence (EDD)</H2>
      <P>EDD is required for higher-risk clients and transactions. It goes beyond standard CDD and typically applies to:</P>
      <BulletList items={[
        'Politically Exposed Persons (PEPs) and their close associates',
        'Clients from higher-risk jurisdictions',
        'Complex ownership structures',
        'Unusual or large transactions without a clear economic rationale',
        'Clients operating in higher-risk sectors'
      ]} />

      <H2 id="str">Suspicious Transaction Reporting</H2>
      <P>
        Reporting entities must assess unusual transactions and activities for possible money laundering, terrorist
        financing and related crimes. A red flag should prompt further review; it does not automatically mean that a
        report must be filed.
      </P>
      <P>
        Where the applicable suspicion or reasonable-grounds-to-suspect test is met, the reporting entity must submit
        the appropriate suspicious transaction or activity report to the UAE Financial Intelligence Unit through goAML,
        in accordance with applicable requirements.
      </P>
      <H3>Common red flags</H3>
      <BulletList items={[
        'Transactions inconsistent with the customer profile',
        'Unusual payment patterns or routing',
        'Reluctance to provide required KYC information',
        'Use of shell companies or unusually complex structures',
        'Requests for anonymity'
      ]} />

      <H2 id="urgent-reporting">Urgent Reporting and UAEFIU Orders</H2>
      <P>
        UAEFIU Regulation No. 1 of 2026 establishes procedures for urgent suspicious transaction reporting and
        responding to suspension, freezing and monitoring orders. The UAEFIU has announced that the regulation takes
        effect on 14 December 2026.
      </P>
      <Callout variant="warning" title="Effective 14 December 2026">
        UAEFIU Regulation No. 1 of 2026 takes effect on 14 December 2026. Reporting entities should review their
        procedures before the effective date.
      </Callout>
      <H3>What is a PSTR?</H3>
      <P>
        A Postponement Suspicious Transaction Report (PSTR) is an urgent report filed through goAML when a reporting
        entity suspects, or has reasonable grounds to suspect, that a transaction, attempted transaction or funds are
        connected to crime, and there is an imminent risk that the funds may be transferred, withdrawn or dissipated.
      </P>
      <P>Under the regulation, a PSTR must be filed promptly, meaning within 24 hours.</P>
      <H3>Is AED 500,000 a minimum reporting threshold?</H3>
      <P>
        No. AED 500,000 is one indicative factor mentioned in the regulation, but it is not a minimum reporting
        threshold. A PSTR may be required for a lower amount when the relevant conditions are met.
      </P>
      <P>A PSTR does not replace existing obligations to submit an STR, SAR or another applicable suspicious report.</P>
      <H3>What types of orders can the UAEFIU issue?</H3>
      <BulletList items={[
        <><strong>Suspension order:</strong> Suspends a transaction for up to 10 business days.</>,
        <><strong>Freezing order:</strong> Freezes specified funds for up to 30 days, subject to extension by the Attorney General or delegate.</>,
        <><strong>Monitoring order:</strong> Requires monitoring of specified transactions or business relationships for up to 30 days.</>
      ]} />
      <P>
        Suspension and freezing orders are immediately binding. The regulation generally requires acknowledgement
        within two hours, subject to the relevant exceptions.
      </P>
      <P>
        The action a reporting entity can take will depend on its role and whether it controls the transaction or
        funds. Businesses should follow the specific order and UAEFIU instructions.
      </P>
      <P>
        Customer communication requirements differ between suspension and freezing orders. Staff should follow the
        relevant requirements and avoid any unauthorised disclosure that could result in tipping-off.
      </P>
      <H3>How can businesses prepare?</H3>
      <p className="text-gray-700 mb-2">Reporting entities should review:</p>
      <BulletList items={[
        'Procedures for identifying and escalating urgent suspicions;',
        'Compliance officer responsibilities and access to goAML;',
        'Arrangements for receiving and responding to UAEFIU communications;',
        'Staff training and confidentiality procedures;',
        'Independent testing of procedures; and',
        'Documentation and recordkeeping arrangements.'
      ]} />
      <P>
        <Link to={FIU_GUIDE} className={linkCls} data-testid="goaml-guide-fiu-detail-link">
          Read the detailed guide: UAE FIU Regulation No. 1 of 2026
        </Link>
      </P>

      <H2 id="aml-policies">AML Policies and Procedures</H2>
      <p className="text-gray-700 mb-2">Every DNFBP should maintain:</p>
      <BulletList items={[
        'A written AML Policy tailored to the business',
        'A documented customer risk assessment framework',
        'Staff training records',
        'Customer files with CDD and EDD documentation',
        'Internal audit and independent review reports',
        'Procedures for urgent PSTR escalation and responding to UAEFIU suspension, freezing and monitoring orders, as applicable'
      ]} />

      <H2 id="risk-assessment">Risk Assessment</H2>
      <P>UAE regulations require every DNFBP to prepare and maintain a business-level AML risk assessment. The assessment should cover customer risk, geographic risk, product and service risk, and delivery channel risk. It must be reviewed periodically and whenever the business changes materially.</P>

      <H2 id="internal-audit">AML Internal Audit</H2>
      <P>
        Regular independent reviews help verify that AML controls are working as intended. An internal AML audit
        typically covers policy adequacy, CDD and EDD sample testing, training coverage, STR filing quality and
        record-keeping. Findings should be reported to senior management and remediated on a timely basis.
      </P>
      <P>
        Independent reviews should also periodically assess whether the business’s procedures for urgent reporting and
        responding to UAEFIU orders are adequate and operating effectively.
      </P>

      <H2 id="mistakes">Common AML Mistakes</H2>
      <BulletList items={[
        'No business-level risk assessment',
        'Incomplete or missing customer files',
        'Lack of ongoing staff training',
        'Failure to screen customers against sanctions and PEP lists',
        'Failure to review red flags properly, or to submit STRs or SARs when the reporting test is met',
        'No documented AML Compliance Officer'
      ]} />

      <H2 id="wpaa-help">How WPAA Can Help</H2>
      <P>Strong AML controls protect UAE businesses from regulatory penalties and reputational damage, and give clients confidence that the business is run responsibly.</P>
      <P>
        WPAA assists DNFBPs with{' '}
        <Link to="/services/goaml-compliance" className={linkCls} data-testid="goaml-guide-service-link">goAML registration</Link>,
        AML policy drafting, business risk assessments, staff training, AML internal audits and remediation support.
      </P>

      <H2 id="sources">Sources</H2>
      <BulletList items={[
        <><a href={REGULATION_PDF} target="_blank" rel="noopener noreferrer" className={linkCls} data-testid="goaml-guide-source-regulation">UAEFIU Regulation No. (1) of 2026 Regarding the System Governing the Postponement or Suspension of Suspicious Transactions, and the Freezing of Funds</a> (official text, as circulated by ADGM)</>,
        <><a href={FIU_ANNOUNCEMENT} target="_blank" rel="noopener noreferrer" className={linkCls} data-testid="goaml-guide-source-announcement">UAEFIU announcement of Regulation No. 1 of 2026</a> (official UAE Financial Intelligence Unit LinkedIn page)</>
      ]} />
    </ArticleShell>
  );
};

export default UAEGoAMLComplianceGuide;
